import type { ReactNode } from "react";
import { useIntl } from "react-intl";
import { Text, Link, WorkflowMobileFooter } from "@wayflyer/flyui";

import { ONBOARDING_WORKFLOW_MESSAGES } from "./messages";
import { ONBOARDING_SUBSTEP_IDS } from "./constants";

const HELP_CENTER_URL = "https://help-center.wayflyer.com";
const HELP_CENTER_BANKING_URL = "https://help-center.wayflyer.com/en/collections/156738-banking-connections";
const HELP_CENTER_FUNDING_REQUIREMENTS_URL =
  "https://help-center.wayflyer.com/en/articles/633716-what-are-the-requirements-to-get-funding-from-wayflyer";
const HELP_CENTER_PLATFORM_URL =
  "https://help-center.wayflyer.com/en/articles/96302-why-does-wayflyer-need-access-to-my-platforms";
const TERMS_OF_BUSINESS_URL = "https://www.wayflyer.com/terms";
const PRIVACY_POLICY_URL = "https://www.wayflyer.com/privacy-notice";

function resolveContactMessage(currentSubstepId: string | undefined) {
  if (currentSubstepId === ONBOARDING_SUBSTEP_IDS.BANK_ACCOUNT) {
    return { messageKey: "footerWorkflowBankingContact" as const, url: HELP_CENTER_BANKING_URL };
  }
  if (currentSubstepId === ONBOARDING_SUBSTEP_IDS.PLATFORMS) {
    return { messageKey: "footerWorkflowPlatformsContact" as const, url: HELP_CENTER_PLATFORM_URL };
  }
  if (currentSubstepId === ONBOARDING_SUBSTEP_IDS.SOFT_CREDIT_CHECK) {
    return { messageKey: "footerWorkflowCreditCheckContact" as const, url: HELP_CENTER_URL };
  }
  return { messageKey: "footerWorkflowContact" as const, url: HELP_CENTER_FUNDING_REQUIREMENTS_URL };
}

export function useWorkflowTrackerFooter(currentSubstepId: string | undefined) {
  const intl = useIntl();
  const { messageKey, url } = resolveContactMessage(currentSubstepId);

  const helpText = (
    <Text size="sm" fontWeight="medium" color="placeholder">
      {intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.footerWorkflowHelpHeading)}
    </Text>
  );

  const contactText = intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES[messageKey], {
    p: (chunks: ReactNode) => (
      <Text size="sm" color="placeholder">
        {chunks}
      </Text>
    ),
    a1: (chunks: ReactNode) => (
      <Link size="small" color="subtle" url={url} external>
        {chunks}
      </Link>
    ),
  });

  const legalText = intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.footerWorkflowLegal, {
    p: (chunks: ReactNode) => (
      <Text size="sm" color="placeholder">
        {chunks}
      </Text>
    ),
    a1: (chunks: ReactNode) => (
      <Link size="small" color="subtle" url={TERMS_OF_BUSINESS_URL} external>
        {chunks}
      </Link>
    ),
    a2: (chunks: ReactNode) => (
      <Link size="small" color="subtle" url={PRIVACY_POLICY_URL} external>
        {chunks}
      </Link>
    ),
  });

  const mobileFooter = (
    <WorkflowMobileFooter
      links={[
        {
          children: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.mobileFooterWorkflowTerms),
          url: TERMS_OF_BUSINESS_URL,
        },
        {
          children: intl.formatMessage(ONBOARDING_WORKFLOW_MESSAGES.mobileFooterWorkflowPrivacy),
          url: PRIVACY_POLICY_URL,
        },
      ]}
    />
  );

  return { helpText, contactText, legalText, mobileFooter };
}
